/*
 * X (Twitter), API v2.
 *
 * Read against the current docs (hosts api.x.com and x.com):
 *   - OAuth 2.0 Authorization Code with PKCE (authorize x.com/i/oauth2/authorize,
 *     token api.x.com/2/oauth2/token, Basic auth for a confidential client):
 *     https://docs.x.com/resources/fundamentals/authentication/oauth-2-0/authorization-code
 *     https://docs.x.com/fundamentals/authentication/oauth-2-0/user-access-token
 *   - GET /2/users/me:            https://docs.x.com/x-api/users/get-my-user
 *   - POST /2/tweets:             https://docs.x.com/x-api/posts/creation-of-a-post
 *   - DELETE /2/tweets/:id:       https://docs.x.com/x-api/posts/delete-post
 *   - GET /2/users/:id/tweets:    https://docs.x.com/x-api/users/get-posts
 *   - POST /2/media/upload (simple, images):  https://docs.x.com/x-api/media/upload-media
 *   - Chunked upload (initialize / {id}/append / {id}/finalize / STATUS):
 *     https://docs.x.com/x-api/media/quickstart/media-upload-chunked
 *
 * Not verified against a live account (verifiedLive stays false). To check:
 *   - Editing is not on the public API, so `posts.edit` is left out.
 *   - A LINK post sends the link inside the text. X counts any link as 23
 *     characters, but the engine checks the message alone against 280, so a
 *     message close to 280 plus a link can still be refused by X.
 *   - Video: the STATUS poll waits for `data.processing_info.state` to be
 *     `succeeded`. If finalize answers without `processing_info` (a very small
 *     file) the poll has nothing to read and runs to its time limit. The docs
 *     say processing_info is returned "if" processing is needed; test a tiny
 *     clip. The poll interval is fixed (the engine cannot follow
 *     `check_after_secs`).
 *   - Segments are 4 MiB (the docs allow up to 5 MB each).
 *   - Photos use the simple `POST /2/media/upload` with media_category
 *     `tweet_image`. Animated GIFs (`tweet_gif`) are not offered.
 *   - Listing asks for `max_results={{count}}`; the API takes 5 to 100 and the
 *     app passes 50.
 *   - Revoking a token is left out: the engine would send the client secret in
 *     the body, and X's revoke call is documented for Basic auth.
 *   - The access token lives two hours; `offline.access` gives the refresh token.
 *   - Posting needs a paid API tier; a free tier answers 402/403.
 */
const API = 'https://api.x.com';
export const X_PRESET = {
    key: 'X',
    name: 'X',
    verifiedLive: false,
    contract: {
        version: 1,
        allowedHosts: ['api.x.com', 'x.com'],
        auth: {
            type: 'oauth2',
            authorizeUrl: 'https://x.com/i/oauth2/authorize',
            tokenUrl: `${API}/2/oauth2/token`,
            scopes: ['tweet.read', 'tweet.write', 'users.read', 'offline.access', 'media.write'],
            scopeSeparator: ' ',
            pkce: true,
            clientSecretIn: 'basic',
            refresh: true,
        },
        tokenPlacement: { in: 'bearer' },
        identity: {
            request: { method: 'GET', url: `${API}/2/users/me` },
            idPath: 'data.id',
            namePath: 'data.username',
            accountId: '{{entry.id}}',
            kind: 'PERSON',
        },
        publish: {
            kinds: ['TEXT', 'LINK', 'PHOTO', 'MULTI_PHOTO', 'VIDEO'],
            request: {
                method: 'POST',
                url: `${API}/2/tweets`,
                json: { text: '{{text}}' },
            },
            perKind: {
                LINK: { text: '{{text}}\n{{link}}' },
                PHOTO: { media: { media_ids: '{{mediaIds}}' } },
                MULTI_PHOTO: { media: { media_ids: '{{mediaIds}}' } },
                VIDEO: { media: { media_ids: '{{mediaIds}}' } },
            },
            resultId: { from: 'body', path: 'data.id' },
            maxTextLength: 280,
            minImages: 2,
            maxImages: 4,
        },
        media: {
            image: {
                type: 'multipart',
                request: {
                    method: 'POST',
                    url: `${API}/2/media/upload`,
                    multipart: { fields: { media_category: 'tweet_image' }, fileField: 'media' },
                },
                mediaIdPath: 'data.id',
            },
            video: {
                type: 'chunked',
                init: {
                    method: 'POST',
                    url: `${API}/2/media/upload/initialize`,
                    json: {
                        media_type: '{{mimeType}}',
                        total_bytes: '{{fileSize}}',
                        media_category: 'tweet_video',
                    },
                },
                mediaIdPath: 'data.id',
                chunkSize: 4 * 1024 * 1024,
                append: {
                    mode: 'request',
                    request: {
                        method: 'POST',
                        url: `${API}/2/media/upload/{{mediaId}}/append`,
                        multipart: { fields: { segment_index: '{{segmentIndex}}' }, fileField: 'media' },
                    },
                },
                finalize: { method: 'POST', url: `${API}/2/media/upload/{{mediaId}}/finalize` },
                poll: {
                    request: {
                        method: 'GET',
                        url: `${API}/2/media/upload?command=STATUS&media_id={{mediaId}}`,
                    },
                    statePath: 'data.processing_info.state',
                    doneValues: ['succeeded'],
                    failedValues: ['failed'],
                    // A clip that needs no processing comes back without processing_info.
                    doneWhenMissing: true,
                    errorPath: 'data.processing_info.error.message',
                    intervalMs: 3_000,
                    maxWaitMs: 5 * 60_000,
                },
            },
        },
        posts: {
            list: {
                request: {
                    method: 'GET',
                    url: `${API}/2/users/{{account.id}}/tweets?max_results={{count}}&tweet.fields=created_at`,
                },
                listPath: 'data',
                idPath: 'id',
                textPath: 'text',
                createdPath: 'created_at',
                createdFormat: 'iso',
            },
            delete: { method: 'DELETE', url: `${API}/2/tweets/{{postId}}` },
        },
        permalink: 'https://x.com/i/web/status/{{postId}}',
        errorMessagePaths: ['detail', 'title', 'error_description', 'message'],
    },
};
//# sourceMappingURL=x.js.map